/**
 * Trip Atlas 3D — every trip as a block in the PazNet axon drawing language.
 *
 * Time runs along the row axis, properties are lanes in depth, and height is
 * days on site. The camera is the axon standard's fixed orthographic view
 * (lib/tripAtlas.ts `axonBasis`): it pans and zooms, never rotates, so an atlas
 * lines up with the rack drawings. Faces are shaded like the drawings (top
 * lightest, front, then side), inferred states are hatched, and red is never a
 * state.
 *
 * Structure follows RackScene3D: three.js loads lazily, colours come from the
 * semantic tokens, the scene rebuilds on data or theme change, and selection or
 * search restyles in place without a rebuild.
 *
 * Built for browsing: frames render only while something moves (pan, damping,
 * a camera flight, a restyle) — an idle atlas costs nothing. Each block is one
 * draw call (faces shaded by vertex colour) sharing a handful of materials, so
 * search and selection swap materials instead of allocating, and one hatch
 * texture serves every block (UVs carry the spacing). Callouts are an HTML HUD layer on
 * vertical leaders, like the drawings' callout boxes, so text stays crisp and
 * readable by screen readers.
 */
import { useEffect, useRef, useState } from 'react';
import type * as THREE_NS from 'three';
import type { OrbitControls as OrbitControlsT } from 'three/examples/jsm/controls/OrbitControls.js';
import {
  COLOR_BANDS,
  LANE_PITCH,
  TRIP_STATES,
  axonBasis,
  blockFill,
  tripMargin,
  toRenderer,
  type AtlasBlock,
  type ColorBy,
  type TripAtlas,
  type TripState,
} from '../../lib/tripAtlas';

type THREE = typeof THREE_NS;

export interface CalloutAnchor {
  tripId: string;
  x: number;
  y: number;
  visible: boolean;
}

export interface TripAtlas3DProps {
  atlas: TripAtlas;
  /** Trips a search matched; null = no search (everything at full strength). */
  highlight: Set<string> | null;
  selectedTripId: string | null;
  /** Trips to show callouts for (selected first). */
  calloutIds: string[];
  /** Bump to frame `fitIds` (or everything when empty). */
  fitNonce: number;
  fitIds: string[];
  onSelect: (tripId: string | null) => void;
  /** Screen anchors for the callout layer, updated as the view moves. */
  onAnchors: (anchors: CalloutAnchor[]) => void;
  /** A trip hovered elsewhere (the result list): outlined in the scene. */
  hoverTripId?: string | null;
  /** A lane label was clicked (a property id, or a year in a by-year atlas). */
  onLane?: (locationId: string) => void;
  /** What block colour means. Repaints in place; the scene is not rebuilt. */
  colorBy?: ColorBy;
}

const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function cssVar(name: string, fallback: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

function useThemeKey(): string {
  const [key, setKey] = useState(() => document.documentElement.dataset.theme ?? 'light');
  useEffect(() => {
    const obs = new MutationObserver(() => setKey(`${document.documentElement.dataset.theme ?? 'light'}:${document.documentElement.dataset.brand ?? ''}`));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-brand'] });
    return () => obs.disconnect();
  }, []);
  return key;
}

function textSprite(THREE: THREE, text: string, color: string, size: number, align: 'left' | 'right' | 'center' = 'left', weight = 500): THREE_NS.Sprite {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const font = `${weight} 56px Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`;
  ctx.font = font;
  const w = Math.ceil(ctx.measureText(text).width) + 16;
  canvas.width = w;
  canvas.height = 76;
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 8, 40);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false }));
  const sw = (w / 76) * size;
  sprite.scale.set(sw, size, 1);
  sprite.center.set(align === 'left' ? 0 : align === 'right' ? 1 : 0.5, 0.5);
  sprite.renderOrder = 10;
  return sprite;
}

/** Hatch like the drawings: ~49°, thin lines on the fill (green on soft green, or ink on white to tint). */
function hatchCanvas(fill: string, ink: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = fill;
  ctx.fillRect(0, 0, 64, 64);
  ctx.strokeStyle = ink;
  ctx.lineWidth = 3;
  for (let i = -64; i <= 128; i += 16) {
    ctx.beginPath();
    ctx.moveTo(i, 64);
    ctx.lineTo(i + 64 / Math.tan((49 * Math.PI) / 180), 0);
    ctx.stroke();
  }
  return c;
}

interface BlockHandles {
  block: AtlasBlock;
  mesh: THREE_NS.Mesh;
  edges: THREE_NS.LineSegments;
}

/** Shared looks: every block points at one of these; restyling swaps pointers. */
interface Looks {
  face: {
    solid: THREE_NS.MeshBasicMaterial;
    solidDim: THREE_NS.MeshBasicMaterial;
    /** State mode: the drawings' green hatch. */
    hatch: THREE_NS.MeshBasicMaterial;
    hatchDim: THREE_NS.MeshBasicMaterial;
    /** Other modes: a neutral hatch tinted by the block's band colour. */
    hatchTint: THREE_NS.MeshBasicMaterial;
    hatchTintDim: THREE_NS.MeshBasicMaterial;
    ghost: THREE_NS.MeshBasicMaterial;
  };
  edge: Map<TripState, { lit: THREE_NS.Material; dim: THREE_NS.Material }>;
  selected: THREE_NS.Material;
  hovered: THREE_NS.Material;
}

interface SceneState {
  THREE: THREE;
  camera: THREE_NS.OrthographicCamera;
  controls: OrbitControlsT;
  blocks: Map<string, BlockHandles>;
  runs: { tripIds: string[]; mats: (THREE_NS.MeshBasicMaterial | THREE_NS.SpriteMaterial)[] }[];
  risers: { tripId: string; mats: THREE_NS.MeshBasicMaterial[] }[];
  /** Day-count badges on long trips. */
  badges: { tripId: string; mat: THREE_NS.SpriteMaterial }[];
  looks: Looks;
  /** Rewrite every block's face colours for a colour mode. */
  paint: (mode: ColorBy) => void;
  fit: (ids: string[], animate?: boolean) => void;
  emitAnchors: () => void;
  invalidate: () => void;
}

export function TripAtlas3D(props: TripAtlas3DProps) {
  const { atlas, highlight, selectedTripId, calloutIds, fitNonce, fitIds, onSelect, onAnchors, hoverTripId = null, onLane, colorBy = 'state' } = props;
  const hostRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<SceneState | null>(null);
  const cb = useRef({ onSelect, onAnchors, onLane });
  cb.current = { onSelect, onAnchors, onLane };
  const liveRef = useRef({ highlight, selectedTripId, calloutIds, fitIds, hoverTripId, colorBy, sceneHover: null as string | null });
  liveRef.current = { ...liveRef.current, highlight, selectedTripId, calloutIds, fitIds, hoverTripId, colorBy };
  const themeKey = useThemeKey();
  // Re-render only when the hovered trip changes; the tooltip follows the
  // pointer by writing its own style.
  const [hoverId, setHoverId] = useState<string | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let disposed = false;
    let cleanup = () => {};

    (async () => {
      let THREE: THREE;
      let OrbitControls: typeof OrbitControlsT;
      try {
        THREE = await import('three');
        ({ OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js'));
      } catch {
        setFailed(true);
        return;
      }
      if (disposed) return;

      let renderer: THREE_NS.WebGLRenderer;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      } catch {
        setFailed(true);
        return;
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(host.clientWidth, host.clientHeight);
      renderer.domElement.style.display = 'block';
      host.appendChild(renderer.domElement);

      const tok = (name: string, fb: string) => new THREE.Color(cssVar(`--${name}`, fb));
      const ink = cssVar('--text', '#28251D');
      const muted = cssVar('--text-muted', '#7A7974');
      const colors = {
        ink: tok('text', '#28251D'),
        muted: tok('text-muted', '#7A7974'),
        border: tok('border', '#D4D1CA'),
        laneA: tok('surface', '#F9F8F5'),
        laneB: tok('surface-2', '#F3F0EC'),
        // Selection is ink, not the theme primary: the corporate theme maps
        // primary to PazNet red, and in the drawing language red is the logo only.
        select: tok('text', '#28251D'),
        success: tok('success', '#437A22'),
        warning: tok('warning', '#964219'),
      };

      const scene = new THREE.Scene();
      const { right, up, back } = axonBasis();
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -2000, 2000);
      // OrbitControls calls lookAt() every frame, which rebuilds the rotation from
      // camera.up. With the axon up vector, lookAt reproduces the axon basis exactly.
      camera.up.set(...up);
      camera.quaternion.setFromRotationMatrix(
        new THREE.Matrix4().makeBasis(new THREE.Vector3(...right), new THREE.Vector3(...up), new THREE.Vector3(...back)),
      );
      const setFrustum = () => {
        const w = host.clientWidth;
        const h = Math.max(host.clientHeight, 1);
        camera.left = -w / 2;
        camera.right = w / 2;
        camera.top = h / 2;
        camera.bottom = -h / 2;
        camera.updateProjectionMatrix();
      };
      setFrustum();

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableRotate = false; // the angle never changes
      controls.screenSpacePanning = true;
      controls.enableDamping = true;
      controls.dampingFactor = 0.12;
      controls.minZoom = 1.5;
      controls.maxZoom = 120;
      controls.mouseButtons = { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
      controls.touches = { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_PAN };

      const disposables: { dispose: () => void }[] = [];
      const track = <T extends { dispose: () => void }>(x: T) => (disposables.push(x), x);
      const pickables: THREE_NS.Object3D[] = [];
      const labelPickables: THREE_NS.Object3D[] = [];
      const v3 = (x: number, y: number, z: number) => new THREE.Vector3(...toRenderer(x, y, z));

      // ── Lanes (the plinth), month grid, labels ─────────────────────────────
      const laneLen = atlas.width + 2;
      for (const lane of atlas.lanes) {
        const geo = track(new THREE.PlaneGeometry(laneLen, LANE_PITCH - 0.3));
        const mat = track(new THREE.MeshBasicMaterial({ color: lane.index % 2 ? colors.laneB : colors.laneA }));
        const plane = new THREE.Mesh(geo, mat);
        plane.position.copy(v3(atlas.width / 2, lane.y, -0.01));
        scene.add(plane);
        const edge = new THREE.LineSegments(track(new THREE.EdgesGeometry(geo)), track(new THREE.LineBasicMaterial({ color: colors.border })));
        edge.position.copy(plane.position);
        scene.add(edge);
        const label = textSprite(THREE, lane.name, ink, 1.35, 'right', 500);
        label.position.copy(v3(-1.6, lane.y, 0.2));
        label.userData = { locationId: lane.locationId };
        scene.add(label);
        labelPickables.push(label);
        if (lane.openItems > 0) {
          const oi = textSprite(THREE, `${lane.openItems} open item${lane.openItems === 1 ? '' : 's'}`, cssVar('--warning', '#964219'), 1, 'right', 400);
          oi.position.copy(v3(-1.6, lane.y + 1.3, 0.2));
          scene.add(oi);
        }
      }
      const grid: number[] = [];
      const yearGrid: number[] = [];
      for (const m of atlas.months) {
        const a = v3(m.x, 0, 0);
        const b = v3(m.x, atlas.depth, 0);
        (m.isYearStart ? yearGrid : grid).push(a.x, a.y, a.z, b.x, b.y, b.z);
        if (!m.label) continue;
        const lab = textSprite(THREE, m.label, m.isYearStart || m === atlas.months[0] ? ink : muted, m.isYearStart ? 1.2 : 0.95, 'left', m.isYearStart ? 500 : 400);
        lab.position.copy(v3(m.x + 0.2, atlas.depth + 1.6, 0));
        scene.add(lab);
      }
      const addLines = (pts: number[], color: THREE_NS.Color) => {
        if (!pts.length) return;
        const g = track(new THREE.BufferGeometry());
        g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
        scene.add(new THREE.LineSegments(g, track(new THREE.LineBasicMaterial({ color }))));
      };
      addLines(grid, colors.border);
      addLines(yearGrid, colors.muted);

      if (atlas.todayX !== null) {
        // A by-year atlas draws Today only across the current year's lane.
        const lane = atlas.todayLane === null ? null : atlas.lanes[atlas.todayLane];
        const y0 = lane ? lane.y - LANE_PITCH / 2 : -0.8;
        const y1 = lane ? lane.y + LANE_PITCH / 2 : atlas.depth + 0.8;
        const a = v3(atlas.todayX, y0, 0.02);
        const b = v3(atlas.todayX, y1, 0.02);
        addLines([a.x, a.y, a.z, b.x, b.y, b.z], colors.muted);
        const t = textSprite(THREE, 'Today', ink, 1.1, 'center', 500);
        // By year, the label sits under the month row so it reads against the months.
        t.position.copy(v3(atlas.todayX, lane ? atlas.depth + 3.4 : y0 - 1, 0));
        scene.add(t);
      }

      // ── Blocks ─────────────────────────────────────────────────────────────
      // One hatch texture for every block; each geometry's UVs carry the spacing.
      const hatchTex = track(new THREE.CanvasTexture(hatchCanvas(cssVar('--success-soft', '#D4DFCC'), cssVar('--success', '#437A22'))));
      hatchTex.colorSpace = THREE.SRGBColorSpace;
      hatchTex.wrapS = hatchTex.wrapT = THREE.RepeatWrapping;
      const tintTex = track(new THREE.CanvasTexture(hatchCanvas('#FFFFFF', 'rgba(40, 37, 29, 0.45)')));
      tintTex.colorSpace = THREE.SRGBColorSpace;
      tintTex.wrapS = tintTex.wrapT = THREE.RepeatWrapping;
      const DIM = 0.12;
      const faceMat = (extra: Partial<THREE_NS.MeshBasicMaterialParameters>) =>
        track(new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, ...extra }));
      const looks: Looks = {
        face: {
          solid: faceMat({}),
          solidDim: faceMat({ opacity: DIM, depthWrite: false }),
          hatch: faceMat({ map: hatchTex }),
          hatchDim: faceMat({ map: hatchTex, opacity: DIM, depthWrite: false }),
          hatchTint: faceMat({ map: tintTex }),
          hatchTintDim: faceMat({ map: tintTex, opacity: DIM, depthWrite: false }),
          // Cancelled: an outline only, but still pickable.
          ghost: faceMat({ opacity: 0, depthWrite: false }),
        },
        edge: new Map(),
        selected: track(new THREE.LineBasicMaterial({ color: colors.select })),
        hovered: track(new THREE.LineBasicMaterial({ color: colors.select, transparent: true, opacity: 0.55 })),
      };
      for (const state of Object.keys(TRIP_STATES) as TripState[]) {
        const st = TRIP_STATES[state];
        const color = tok(st.edge, ink);
        const make = (opacity: number) =>
          track(
            st.dash
              ? new THREE.LineDashedMaterial({ color, dashSize: st.dash[0], gapSize: st.dash[1], transparent: true, opacity })
              : new THREE.LineBasicMaterial({ color, transparent: true, opacity }),
          );
        looks.edge.set(state, { lit: make(1), dim: make(0.18) });
      }

      // Face order: +x (right side), −x, +y (back), −y (front, toward viewer), +z (top), −z.
      const SHADE = [0.86, 0.86, 0.93, 0.93, 1, 1];
      const HATCH_TILE = 0.8;
      const white = new THREE.Color(1, 1, 1);
      const tokenColors = new Map<string, THREE_NS.Color>();
      const tokenColor = (name: string) => {
        let c = tokenColors.get(name);
        if (!c) tokenColors.set(name, (c = tok(name, '#FFFFFF')));
        return c;
      };
      const blocks = new Map<string, BlockHandles>();
      const shade = new THREE.Color();
      /** Face colours for a mode: top lightest, then front, then side, like the drawings. */
      const paint = (mode: ColorBy) => {
        for (const h of blocks.values()) {
          const b = h.block;
          const style = TRIP_STATES[b.state];
          const fill = blockFill(mode, b, atlas.byId.get(b.tripId));
          // The state hatch carries its own colour; tinted hatch and solids take the fill.
          const base = mode === 'state' && style.hatch ? white : fill ? tokenColor(fill) : colors.laneA;
          const attr = (h.mesh.geometry as THREE_NS.BufferGeometry).getAttribute('color') as THREE_NS.BufferAttribute;
          for (let f = 0; f < 6; f += 1) {
            shade.copy(base).multiplyScalar(SHADE[f]);
            for (let v = 0; v < 4; v += 1) attr.setXYZ(f * 4 + v, shade.r, shade.g, shade.b);
          }
          attr.needsUpdate = true;
        }
      };
      for (const b of atlas.blocks) {
        const style = TRIP_STATES[b.state as TripState];
        const geo = track(new THREE.BoxGeometry(b.w, b.d, b.h));
        geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(24 * 3), 3));
        if (style.hatch) {
          // BoxGeometry(width=w, height=d, depth=h): ±x faces span (h, d), ±y (w, h), ±z (w, d).
          const dims: [number, number][] = [[b.h, b.d], [b.h, b.d], [b.w, b.h], [b.w, b.h], [b.w, b.d], [b.w, b.d]];
          const uv = geo.getAttribute('uv') as THREE_NS.BufferAttribute;
          for (let f = 0; f < 6; f += 1) {
            for (let v = 0; v < 4; v += 1) {
              const i = f * 4 + v;
              uv.setXY(i, uv.getX(i) * (dims[f][0] / HATCH_TILE), uv.getY(i) * (dims[f][1] / HATCH_TILE));
            }
          }
          uv.needsUpdate = true;
        }
        const mesh = new THREE.Mesh(geo, style.opacity === 0 ? looks.face.ghost : style.hatch ? looks.face.hatch : looks.face.solid);
        mesh.position.copy(v3(b.x + b.w / 2, b.y + b.d / 2, b.z + b.h / 2));
        mesh.userData = { tripId: b.tripId };
        scene.add(mesh);
        pickables.push(mesh);

        const edges = new THREE.LineSegments(track(new THREE.EdgesGeometry(geo)), looks.edge.get(b.state)!.lit);
        if (style.dash) edges.computeLineDistances();
        edges.position.copy(mesh.position);
        scene.add(edges);
        blocks.set(b.tripId, { block: b, mesh, edges });
      }
      paint(liveRef.current.colorBy);

      // ── Long trips: a day-count badge over every visit of 4+ days ──────────
      const badges: SceneState['badges'] = [];
      for (const b of atlas.blocks) {
        if (b.length !== 'long' || b.state === 'cancelled') continue;
        const tag = textSprite(THREE, `${b.days} days`, ink, 1, 'center', 600);
        tag.position.copy(v3(b.x + b.w / 2, b.y + b.d / 2, b.z + b.h + (b.runId ? 2.3 : 1.1)));
        scene.add(tag);
        badges.push({ tripId: b.tripId, mat: tag.material });
      }

      // ── Runs: links between stops, arcing over the lanes ────────────────────
      const runHandles: SceneState['runs'] = [];
      for (const run of atlas.runs) {
        // One material per run, so dimming a run is one property write.
        const runMat = track(new THREE.MeshBasicMaterial({ color: colors.success, transparent: true }));
        const mats: (THREE_NS.MeshBasicMaterial | THREE_NS.SpriteMaterial)[] = [runMat];
        const pts = run.tripIds.map((id) => {
          const b = atlas.blockById.get(id)!;
          return v3(b.x + b.w / 2, b.y + b.d / 2, b.z + b.h);
        });
        for (let i = 0; i + 1 < pts.length; i += 1) {
          const a = pts[i];
          const c = pts[i + 1];
          const mid = a.clone().lerp(c, 0.5);
          mid.z += 3 + a.distanceTo(c) * 0.12;
          const curve = new THREE.QuadraticBezierCurve3(a, mid, c);
          // Dashed (5,3) when a stop is not completed — "partly inferred" in the drawings.
          const segs = run.complete ? [[0, 1]] : Array.from({ length: 12 }, (_, k) => [k / 12, k / 12 + 0.6 / 12]);
          for (const [t0, t1] of segs) {
            const sub = new THREE.CatmullRomCurve3(Array.from({ length: 9 }, (_, k) => curve.getPoint(t0 + ((t1 - t0) * k) / 8)));
            const tube = new THREE.Mesh(track(new THREE.TubeGeometry(sub, 24, run.complete ? 0.14 : 0.1, 6, false)), runMat);
            scene.add(tube);
          }
        }
        // Stop numbers above each block.
        run.tripIds.forEach((id, i) => {
          const b = atlas.blockById.get(id)!;
          const n = textSprite(THREE, `${i + 1}`, cssVar('--success', '#437A22'), 1.1, 'center', 500);
          n.position.copy(v3(b.x + b.w / 2, b.y + b.d / 2, b.z + b.h + 1.1));
          scene.add(n);
          mats.push(n.material);
        });
        runHandles.push({ tripIds: run.tripIds, mats });
      }

      // ── Open items: risers with end dots on the latest visit ───────────────
      const riserHandles: SceneState['risers'] = [];
      const riserMats = new Map<string, THREE_NS.MeshBasicMaterial>();
      for (const r of atlas.risers) {
        // Risers on one visit share a material (they dim together).
        const riserMat = riserMats.get(r.tripId) ?? track(new THREE.MeshBasicMaterial({ color: colors.warning, transparent: true }));
        const isNew = !riserMats.has(r.tripId);
        riserMats.set(r.tripId, riserMat);
        const stemMat = riserMat;
        const dotMat = riserMat;
        const stem = new THREE.Mesh(track(new THREE.CylinderGeometry(0.05, 0.05, r.h, 6)), stemMat);
        stem.rotation.x = Math.PI / 2;
        stem.position.copy(v3(r.x, r.y, r.z + r.h / 2));
        const dot = new THREE.Mesh(track(new THREE.SphereGeometry(0.2, 12, 8)), dotMat);
        dot.position.copy(v3(r.x, r.y, r.z + r.h));
        scene.add(stem, dot);
        if (isNew) riserHandles.push({ tripId: r.tripId, mats: [dotMat] });
      }

      // ── Framing ────────────────────────────────────────────────────────────
      const rightV = new THREE.Vector3(...right);
      const upV = new THREE.Vector3(...up);
      const backV = new THREE.Vector3(...back);
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      let flight: { t: number; fromT: THREE_NS.Vector3; toT: THREE_NS.Vector3; fromZ: number; toZ: number } | null = null;
      // Until the user pans or zooms, the view keeps itself framed (layout can
      // settle after mount, and the window can resize).
      let userMoved = false;
      let lastFit: string[] = [];
      const place = (target: THREE_NS.Vector3) => {
        controls.target.copy(target);
        camera.position.copy(target.clone().addScaledVector(backV, 500));
      };
      const fit = (ids: string[], animate = true) => {
        userMoved = false;
        lastFit = ids;
        const chosen = (ids.length ? ids : atlas.blocks.map((b) => b.tripId)).map((id) => atlas.blockById.get(id)).filter(Boolean) as AtlasBlock[];
        const corners: THREE_NS.Vector3[] = [];
        if (chosen.length) {
          for (const b of chosen) for (const dx of [0, b.w]) for (const dy of [0, b.d]) for (const dz of [0, b.h + 2]) corners.push(v3(b.x + dx, b.y + dy, b.z + dz));
          // Keep the month labels (in front of the first lane) under the chosen blocks in view.
          if (ids.length) for (const b of chosen) corners.push(v3(b.x, atlas.depth + 2.2, 0), v3(b.x + b.w, atlas.depth + 2.2, 0));
        }
        if (!ids.length) {
          // Lane labels are right-aligned at x −1.6; ~0.84 units per character at size 1.35.
          const labelLen = Math.max(8, ...atlas.lanes.map((l) => l.name.length)) * 0.84;
          for (const lane of atlas.lanes) corners.push(v3(-1.6 - lane.name.length * 0.84, lane.y, 0));
          corners.push(v3(-1.6 - labelLen, 0, 0), v3(atlas.width, atlas.depth + 2.5, 0), v3(atlas.width, 0, 0));
        }
        if (!corners.length) corners.push(v3(0, 0, 0));
        // Centre on the projected bounding box (not the mean, which the busy months would pull).
        let minR = Infinity, maxR = -Infinity, minU = Infinity, maxU = -Infinity;
        for (const p of corners) {
          const r = p.dot(rightV);
          const u = p.dot(upV);
          minR = Math.min(minR, r); maxR = Math.max(maxR, r);
          minU = Math.min(minU, u); maxU = Math.max(maxU, u);
        }
        const hw = Math.max(1, (maxR - minR) / 2);
        const hh = Math.max(1, (maxU - minU) / 2);
        // Frame into the part of the canvas the HUD leaves clear: the legend on
        // the right (md and up), the Fit button and callouts above, the title below.
        const pad = viewPadding(ids.length);
        const availW = Math.max(120, host.clientWidth - pad.left - pad.right);
        const availH = Math.max(120, host.clientHeight - pad.top - pad.bottom);
        const margin = ids.length ? 1.12 : 1.04;
        const zoom = Math.max(controls.minZoom, Math.min(ids.length ? 22 : 60, availW / (2 * hw * margin), availH / (2 * hh * margin)));
        const dx = (pad.left - pad.right) / 2;
        const dy = (pad.top - pad.bottom) / 2;
        const c = rightV
          .clone()
          .multiplyScalar((minR + maxR) / 2 - dx / zoom)
          .addScaledVector(upV, (minU + maxU) / 2 + dy / zoom);
        if (reduceMotion || !animate) {
          flight = null;
          place(c);
          camera.zoom = zoom;
          camera.updateProjectionMatrix();
          emitAnchors();
          invalidate();
          return;
        }
        flight = { t: 0, fromT: controls.target.clone(), toT: c, fromZ: camera.zoom, toZ: zoom };
        invalidate();
      };

      // With results, leave room for the callout boxes (to the right of their
      // anchors) and their stack (above).
      const viewPadding = (hits = 0) => {
        const wide = host.clientWidth >= 720;
        const callouts = Math.min(hits, 5);
        return {
          left: 16,
          right: (wide ? 275 : 12) + (callouts && wide ? 200 : 0),
          top: 56 + callouts * 34,
          bottom: wide ? 70 : 64,
        };
      };

      // ── Callout anchors (the HUD layer) ────────────────────────────────────
      let lastAnchorKey = '';
      const emitAnchors = () => {
        const w = host.clientWidth;
        const h = host.clientHeight;
        const anchors = liveRef.current.calloutIds.map((id) => {
          const b = atlas.blockById.get(id);
          if (!b) return { tripId: id, x: 0, y: 0, visible: false };
          // The front-left top edge, like the drawings' anchor on the rack's front-left frame.
          const p = v3(b.x, b.y + b.d, b.z + b.h).project(camera);
          const x = ((p.x + 1) / 2) * w;
          const y = ((1 - p.y) / 2) * h;
          return { tripId: id, x, y, visible: x >= 0 && x <= w && y >= 0 && y <= h };
        });
        // Report only real changes: the pan/zoom loop fires every frame.
        const key = anchors.map((a) => `${a.tripId}:${Math.round(a.x)}:${Math.round(a.y)}:${a.visible ? 1 : 0}`).join('|');
        if (key === lastAnchorKey) return;
        lastAnchorKey = key;
        cb.current.onAnchors(anchors);
      };

      // ── Picking and hover ──────────────────────────────────────────────────
      const raycaster = new THREE.Raycaster();
      const pointer = new THREE.Vector2();
      const pick = (ev: { clientX: number; clientY: number }) => {
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.set(((ev.clientX - rect.left) / rect.width) * 2 - 1, -((ev.clientY - rect.top) / rect.height) * 2 + 1);
        raycaster.setFromCamera(pointer, camera);
        // Blocks win over lane labels.
        return raycaster.intersectObjects(pickables, false)[0] ?? raycaster.intersectObjects(labelPickables, false)[0];
      };
      let down: { x: number; y: number } | null = null;
      const onDown = (ev: PointerEvent) => { down = { x: ev.clientX, y: ev.clientY }; };
      const onUp = (ev: PointerEvent) => {
        if (!down || Math.hypot(ev.clientX - down.x, ev.clientY - down.y) > 5) return;
        down = null;
        const hit = pick(ev);
        const laneId = hit?.object.userData.locationId as string | undefined;
        if (laneId) {
          cb.current.onLane?.(laneId);
          return;
        }
        cb.current.onSelect((hit?.object.userData.tripId as string | undefined) ?? null);
      };
      // Hover: one raycast per frame at most, however fast the pointer moves.
      let pendingMove: PointerEvent | null = null;
      let moveQueued = false;
      const setSceneHover = (id: string | null) => {
        if (liveRef.current.sceneHover === id) return;
        liveRef.current.sceneHover = id;
        setHoverId(id);
        applyStyle();
      };
      const onMove = (ev: PointerEvent) => {
        pendingMove = ev;
        if (moveQueued) return;
        moveQueued = true;
        requestAnimationFrame(() => {
          moveQueued = false;
          const e = pendingMove;
          if (!e || disposed) return;
          const hit = pick(e);
          const id = (hit?.object.userData.tripId as string | undefined) ?? null;
          const isLane = Boolean(hit?.object.userData.locationId);
          renderer.domElement.style.cursor = id || isLane ? 'pointer' : 'grab';
          const rect = host.getBoundingClientRect();
          if (tipRef.current) {
            tipRef.current.style.left = `${e.clientX - rect.left + 14}px`;
            tipRef.current.style.top = `${e.clientY - rect.top + 14}px`;
          }
          setSceneHover(id);
        });
      };
      const onLeave = () => {
        pendingMove = null;
        setSceneHover(null);
      };
      renderer.domElement.addEventListener('pointerdown', onDown);
      renderer.domElement.addEventListener('pointerup', onUp);
      renderer.domElement.addEventListener('pointermove', onMove);
      renderer.domElement.addEventListener('pointerleave', onLeave);
      const onChange = () => {
        emitAnchors();
        invalidate();
      };
      controls.addEventListener('change', onChange);
      const onStart = () => { userMoved = true; flight = null; };
      controls.addEventListener('start', onStart);

      const resize = new ResizeObserver(() => {
        renderer.setSize(host.clientWidth, Math.max(host.clientHeight, 1));
        setFrustum();
        if (!userMoved) fit(lastFit, false);
        emitAnchors();
        invalidate();
      });
      resize.observe(host);

      // ── Render on demand ───────────────────────────────────────────────────
      // A frame is drawn only when something changed; it keeps going only while
      // a flight or the controls' damping is still moving the camera.
      let frame = 0;
      let queued = false;
      const tick = () => {
        queued = false;
        let moving = false;
        if (flight) {
          flight.t = Math.min(1, flight.t + 0.06);
          const e = 1 - Math.pow(1 - flight.t, 3);
          place(flight.fromT.clone().lerp(flight.toT, e));
          camera.zoom = flight.fromZ + (flight.toZ - flight.fromZ) * e;
          camera.updateProjectionMatrix();
          emitAnchors();
          if (flight.t >= 1) flight = null;
          moving = true;
        }
        if (controls.update()) moving = true;
        renderer.render(scene, camera);
        if (moving) invalidate();
      };
      function invalidate() {
        if (queued || disposed) return;
        queued = true;
        frame = requestAnimationFrame(tick);
      }

      place(new THREE.Vector3());
      stateRef.current = { THREE, camera, controls, blocks, runs: runHandles, risers: riserHandles, badges, looks, paint, fit, emitAnchors, invalidate };
      fit(liveRef.current.fitIds);
      applyStyle();
      invalidate();

      cleanup = () => {
        cancelAnimationFrame(frame);
        resize.disconnect();
        controls.removeEventListener('change', onChange);
        controls.removeEventListener('start', onStart);
        renderer.domElement.removeEventListener('pointerdown', onDown);
        renderer.domElement.removeEventListener('pointerup', onUp);
        renderer.domElement.removeEventListener('pointermove', onMove);
        renderer.domElement.removeEventListener('pointerleave', onLeave);
        controls.dispose();
        scene.traverse((o) => {
          if (o instanceof THREE.Sprite) {
            o.material.map?.dispose();
            o.material.dispose();
          }
        });
        disposables.forEach((d) => d.dispose());
        renderer.dispose();
        renderer.domElement.remove();
        stateRef.current = null;
      };
    })();

    return () => {
      disposed = true;
      cleanup();
    };
    // Restyling reads live props through a ref; rebuild only on data or theme.
  }, [atlas, themeKey]);

  /**
   * Search dims non-matches to ghosts; the selection (and a hovered trip) takes
   * an ink outline. Pointer swaps only — nothing is allocated per restyle.
   */
  function applyStyle() {
    const s = stateRef.current;
    if (!s) return;
    const { highlight: hl, selectedTripId: sel, hoverTripId: listHover, sceneHover, colorBy: mode } = liveRef.current;
    const lit = (id: string) => !hl || hl.has(id) || id === sel;
    const { face, edge, selected, hovered } = s.looks;
    const hatchOn = mode === 'state' ? face.hatch : face.hatchTint;
    const hatchOff = mode === 'state' ? face.hatchDim : face.hatchTintDim;
    for (const [id, h] of s.blocks) {
      const on = lit(id);
      const st = TRIP_STATES[h.block.state];
      h.mesh.material = st.opacity === 0 ? face.ghost : st.hatch ? (on ? hatchOn : hatchOff) : on ? face.solid : face.solidDim;
      h.edges.material = id === sel ? selected : id === listHover || id === sceneHover ? hovered : on ? edge.get(h.block.state)!.lit : edge.get(h.block.state)!.dim;
    }
    for (const r of s.runs) {
      const on = !hl || r.tripIds.some((id) => hl.has(id) || id === sel);
      r.mats.forEach((m) => (m.opacity = on ? 1 : 0.12));
    }
    for (const r of s.risers) r.mats.forEach((m) => (m.opacity = lit(r.tripId) ? 1 : 0.15));
    for (const b of s.badges) b.mat.opacity = lit(b.tripId) ? 1 : 0.15;
    s.emitAnchors();
    s.invalidate();
  }

  useEffect(applyStyle, [highlight, selectedTripId, calloutIds, hoverTripId]);

  useEffect(() => {
    stateRef.current?.paint(colorBy);
    // applyStyle reads live props (including the new mode) through the ref.
    applyStyle();
  }, [colorBy]);

  useEffect(() => {
    stateRef.current?.fit(fitIds);
    // fitIds is read at the moment of the nonce bump.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitNonce]);

  const hoverTrip = hoverId ? atlas.byId.get(hoverId) : null;
  const hoverBlock = hoverId ? atlas.blockById.get(hoverId) : null;

  return (
    <div className="absolute inset-0">
      <div ref={hostRef} className="absolute inset-0 touch-none" aria-hidden="true" />
      {failed && (
        <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-text-muted">
          3D view unavailable in this browser. The list beside it has every trip.
        </div>
      )}
      <div
        ref={tipRef}
        className={`pointer-events-none absolute z-20 max-w-[260px] rounded border border-border bg-surface px-2.5 py-1.5 text-xs shadow-sm ${hoverTrip && hoverBlock ? '' : 'hidden'}`}
      >
        {hoverTrip && hoverBlock && (
          <>
          <p className="font-medium text-text">{hoverTrip.location_name}</p>
          <p className="text-text-muted">
            <span className="font-mono">{hoverTrip.project_number}</span> · {hoverTrip.start_date}
            {hoverBlock.days > 1 ? ` · ${hoverBlock.days} days` : ''}
          </p>
          <p className="text-text-muted">{TRIP_STATES[hoverBlock.state].label}</p>
          {hoverBlock.length === 'long' && <p className="font-medium text-text">{COLOR_BANDS.length[2].label} on site</p>}
          {hoverTrip.finance ? (
            <p className="text-text-muted">
              {hoverTrip.finance.revenue === null ? 'No deal amount' : money(hoverTrip.finance.revenue)}
              {hoverTrip.finance.cost === null
                ? ' · cost not in Zoho'
                : ` · ${money((hoverTrip.finance.revenue ?? 0) - hoverTrip.finance.cost)} profit${tripMargin(hoverTrip.finance) === null ? '' : ` (${Math.round(tripMargin(hoverTrip.finance)! * 100)}%)`}`}
            </p>
          ) : (
            <p className="text-text-muted">No Zoho deal linked</p>
          )}
          </>
        )}
      </div>
    </div>
  );
}
